<script setup lang="ts">
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { DatePicker } from 'v-calendar'
import { CheckCircle, XCircle, Loader2, ScanEye, ChevronDown, CalendarPlus } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { formatTimeFromNow } from '@/utils/datetime'
import { formatDate, requestDateRow, daysUntil, MIN_CAPTURE_DAYS, DAY_MS } from './format'
import LayerRequestParams from './LayerRequestParams.vue'
import LayerGeometryStats from './LayerGeometryStats.vue'
import LayerQuotaImpact from './LayerQuotaImpact.vue'
import type { PendingLayer, OrgQuota, PricingEstimate } from './types'

const props = defineProps<{
  layer: PendingLayer
  orgName: string
  selected: boolean
  processing: boolean
  quota?: OrgQuota | null
  quotaLoading?: boolean
  pricing?: PricingEstimate | null
  windowError?: string
}>()

const store = useStore()
const tz = computed<string | null>(() => store.getters['user/getUserSettings']?.tz ?? null)

const emit = defineEmits<{
  (e: 'select'): void
  (e: 'zoom'): void
  (e: 'approve'): void
  (e: 'reject'): void
  (e: 'update-window', window: { start: Date; end: Date }): void
}>()

const detailsOpen = ref(false)
const windowOpen = ref(false)
const windowRange = ref<{ start: Date; end: Date } | null>(null)
const disabledDates = ref([{ end: new Date() }])

const windowMs = computed(() => windowRange.value
  ? windowRange.value.end.getTime() - windowRange.value.start.getTime()
  : 0)
const windowDays = computed(() => Math.round(windowMs.value / DAY_MS))
const windowValid = computed(() => windowMs.value >= MIN_CAPTURE_DAYS * DAY_MS)
const sameInstant = (d: Date, iso?: string | null) => !!iso && d.getTime() === new Date(iso).getTime()
const windowChanged = computed(() => !!windowRange.value && (
  !sameInstant(windowRange.value.start, props.layer.data?.start_time) ||
  !sameInstant(windowRange.value.end, props.layer.data?.end_time)))

const openWindowEditor = (open: boolean) => {
  if (open) {
    disabledDates.value = [{ end: new Date() }]
    const { start_time, end_time } = props.layer.data ?? {}
    windowRange.value = start_time && end_time ? { start: new Date(start_time), end: new Date(end_time) } : null
  }
  windowOpen.value = open
}

const applyWindow = () => {
  if (!windowRange.value || !windowValid.value) return
  emit('update-window', windowRange.value)
  windowOpen.value = false
}

const dateRow = computed(() => requestDateRow(props.layer, tz.value))
const windowState = computed(() => {
  const data = props.layer.data
  if (props.layer.layer_type !== 'tasking' || !data?.start_time) return null
  const toEnd = daysUntil(data.end_time)
  const toStart = daysUntil(data.start_time)
  if (toEnd != null && toEnd < 0) return { text: `elapsed ${-toEnd} days ago`, tone: 'text-destructive' }
  const effectiveDays = toEnd != null && toStart != null ? toEnd - Math.max(toStart, 0) : null
  if (effectiveDays != null && effectiveDays < MIN_CAPTURE_DAYS) return { text: `${effectiveDays}-day window, ${MIN_CAPTURE_DAYS} required`, tone: 'text-destructive' }
  if (toStart != null && toStart < 0) return { text: `started ${-toStart} days ago`, tone: 'text-amber-600 dark:text-amber-400' }
  if (toStart != null) return { text: `starts in ${toStart} days`, tone: 'text-muted-foreground' }
  return null
})
const requester = computed(() =>
  props.layer.member_name ? `${props.layer.member_name} (${props.layer.member_email})` : props.layer.member_email)
</script>

<template>
  <div
    :class="['border rounded-lg bg-card p-4 cursor-pointer transition-colors hover:border-primary', selected ? 'border-primary' : '']"
    @click="emit('select')"
  >
    <div class="flex items-center justify-between gap-2">
      <div class="flex items-center gap-2 min-w-0">
        <span class="font-medium truncate">{{ layer.layer_name || 'Unnamed Layer' }}</span>
      </div>
      <Button variant="ghost" size="sm" class="shrink-0" title="Zoom to AOI" :disabled="!layer.geometry" @click.stop="emit('zoom')">
        <ScanEye class="h-4 w-4 mr-1" />
        Zoom
      </Button>
    </div>

    <div class="mt-2 space-y-1 text-sm">
      <div class="font-medium truncate">{{ orgName }}</div>
      <div class="truncate">{{ requester || '--' }}</div>
      <div><span>Requested {{ formatDate(layer.created_at, tz) }}<span class="text-muted-foreground"> · {{ formatTimeFromNow(layer.created_at) }}</span></span></div>
      <div v-if="dateRow" class="flex items-center gap-2 flex-wrap">
        <span>{{ dateRow.label }} {{ dateRow.value }}<span v-if="windowState" :class="['ml-1', windowState.tone]">· {{ windowState.text }}</span></span>
        <Popover v-if="layer.layer_type === 'tasking'" :open="windowOpen" @update:open="openWindowEditor">
          <PopoverTrigger as-child>
            <Button variant="ghost" size="sm" class="h-6 px-2 text-xs" :disabled="processing" @click.stop>
              <CalendarPlus class="h-3 w-3 mr-1" />
              Change
            </Button>
          </PopoverTrigger>
          <PopoverContent class="p-1 w-auto" @click.stop>
            <DatePicker
              class="font-sans"
              borderless
              trim-weeks
              v-model.range="windowRange"
              :step="1"
              :disabled-dates="disabledDates"
            />
            <div class="flex items-center justify-between gap-2 px-2 pb-2 text-xs">
              <span :class="windowValid ? 'text-muted-foreground' : 'text-destructive'">
                {{ windowDays }} days<template v-if="!windowValid"> · min {{ MIN_CAPTURE_DAYS }}</template>
              </span>
              <Button size="sm" class="h-7" :disabled="!windowValid || !windowChanged" @click.stop="applyWindow">Apply</Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div v-if="windowError" class="text-xs text-destructive">{{ windowError }}</div>
    </div>

    <div class="mt-5">
      <LayerQuotaImpact :layer="layer" :quota="quota" :loading="quotaLoading" :pricing="pricing" mode="bar" />
    </div>

    <div class="mt-7 flex items-end justify-between gap-4">
      <Collapsible v-model:open="detailsOpen" class="min-w-0 flex-1">
        <CollapsibleTrigger class="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" @click.stop>
          <ChevronDown :class="['h-3 w-3 transition-transform', { 'rotate-180': detailsOpen }]" />
          Details
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2 pt-2 border-t border-border/50">
            <LayerRequestParams :layer="layer" />
            <LayerGeometryStats :geometry="layer.geometry" :planet-area-km2="pricing?.area_km2 ?? null" />
            <LayerQuotaImpact :layer="layer" :quota="quota" :loading="quotaLoading" :pricing="pricing" mode="details" />
          </div>
        </CollapsibleContent>
      </Collapsible>

      <div class="flex items-center gap-2 shrink-0">
        <Button variant="destructive" size="sm" :disabled="processing" @click.stop="emit('reject')">
          <XCircle class="h-4 w-4 mr-1" />
          Reject
        </Button>
        <Button variant="default" size="sm" :disabled="processing" @click.stop="emit('approve')">
          <CheckCircle v-if="!processing" class="h-4 w-4 mr-1" />
          <Loader2 v-else class="h-4 w-4 mr-1 animate-spin" />
          Approve
        </Button>
      </div>
    </div>
  </div>
</template>
